import { AriaRole, ReactNode, useCallback, useRef } from "react";

import { AnalyticsCommonProps } from "hooks/useAnalytics";
import { withTestId } from "utils/withTestId";

import { BoxProps } from "../../Box";
import { TypographyProps } from "../../Typography";
import { CardHeaderProps } from "../components/Header";
import { CardSize } from "../types";
import CardCollapsibleContent from "./components/Content";
import CardCollapsibleHeader from "./components/Header";
import { CardCollapsibleAriaLevel } from "./components/types";
import CardCollapsibleWrapper from "./components/Wrapper";

type CardCollapsibleRole = Extract<
  AriaRole,
  "region" | "article" | "group" | "complementary" | "treeitem"
>;

type CardCollapsibleProps = {
  children: ReactNode;
  fullbleed?: boolean;
  variant?: "secondary" | "primary";
  size?: CardSize;
  title: ReactNode;
  titlePrefix?: ReactNode;
  titleTag?: TypographyProps["tag"];
  titleVariant?: CardHeaderProps["titleVariant"];
  className?: string;
  headerClassName?: string;
  headerSuffix?: ReactNode;
  titleSuffix?: ReactNode;
  contentGap?: BoxProps["gap"];
  isCollapsed: boolean;
  onToggle: () => void;
  role?: CardCollapsibleRole;
  ariaLevel: CardCollapsibleAriaLevel;
  ariaLabel: string;
  headerAlign?: BoxProps["align"];
  scrollIntoView?: boolean;
  "data-testid"?: string;
} & Pick<BoxProps, "fullHeight" | "fullWidth" | "margin" | "padding"> &
  AnalyticsCommonProps;

const CardCollapsible = ({
  headerAlign = "center",
  children,
  fullbleed,
  variant,
  size,
  title,
  titlePrefix,
  titleVariant,
  className,
  headerClassName,
  headerSuffix,
  titleSuffix,
  titleTag,
  ariaLevel,
  ariaLabel,
  isCollapsed,
  onToggle,
  role,
  contentGap,
  analyticsPage,
  analyticsTitle,
  analyticsProps,
  scrollIntoView,
  "data-testid": dataTestId,
  ...restBoxProps
}: CardCollapsibleProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    if (scrollIntoView && isCollapsed) {
      const scroll = () => {
        containerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        containerRef.current?.removeEventListener("transitionend", scroll);
      };

      containerRef.current?.addEventListener("transitionend", scroll);
    }

    onToggle();
  }, [scrollIntoView, isCollapsed, onToggle]);

  return (
    <CardCollapsibleWrapper
      ref={containerRef}
      variant={variant}
      size={size}
      className={className}
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      role={role}
      {...withTestId(dataTestId)}
      {...restBoxProps}
    >
      {title && (
        <CardCollapsibleHeader
          align={headerAlign}
          prefix={titlePrefix}
          titleTag={titleTag}
          titleVariant={titleVariant}
          titleSuffix={titleSuffix}
          suffix={headerSuffix}
          ariaLevel={ariaLevel}
          ariaLabel={ariaLabel}
          className={headerClassName}
          analyticsPage={analyticsPage}
          analyticsTitle={analyticsTitle}
          analyticsProps={analyticsProps}
        >
          {title}
        </CardCollapsibleHeader>
      )}
      <CardCollapsibleContent hasHeader={!!title} fullbleed={fullbleed} gap={contentGap}>
        {children}
      </CardCollapsibleContent>
    </CardCollapsibleWrapper>
  );
};

CardCollapsible.displayName = "DS.Card.Collapsible";

export default CardCollapsible;
